import {
  ShopHeroBackdrop,
  ShopHeroCopy,
  ShopHeroScrollCue,
  ShopHeroVeil,
  useHeroMotion,
} from "./hero";

/**
 * `id` de la rejilla de productos. Vive aquí porque quien lo NECESITA es
 * la flecha de bajar; `ShopPage` solo lo cuelga del destino. Con la
 * cadena escrita a mano en los dos sitios, renombrar uno deja la flecha
 * apuntando a ninguna parte y nadie se entera hasta que alguien la pulsa.
 */
export const SHOP_PRODUCTS_ID = "productos";

/**
 * La portada de la tienda.
 *
 * ── Qué se pidió ─────────────────────────────────────────────────────
 * Que tuviera la esencia de `itsthebest1111.com`. Lo que había era un
 * titular sobre crema que podría ser de cualquier tienda de suplementos.
 * Lo que hay ahora es el vídeo de la landing, sus textos y su gesto de
 * entrada, con parallax y una flecha que baja al catálogo.
 *
 * ── Cuatro capas, y el orden importa ─────────────────────────────────
 *   1. `ShopHeroBackdrop` — vídeo, retícula, resplandor y flotantes.
 *      Es la única capa que se mueve con el desplazamiento.
 *   2. `ShopHeroVeil` — el velo medido. Va SIEMPRE entre el fondo y el
 *      texto, nunca al revés: es lo que hace que la legibilidad se pueda
 *      calcular en vez de mirarla y decidir que "se ve bien".
 *   3. `ShopHeroCopy` — el texto, con su propio panel de tinta.
 *   4. `ShopHeroScrollCue` — la flecha.
 *
 * Cualquier capa decorativa nueva va DEBAJO del velo o hay que rehacer la
 * medición que documenta `ShopHeroVeil`.
 *
 * ── El movimiento se decide UNA vez, aquí ────────────────────────────
 * `useHeroMotion` se consulta en este componente y el resultado baja como
 * prop. Con el hook repetido en cada pieza habría cinco suscripciones a
 * la misma media query y, peor, la posibilidad de que una se quedara sin
 * enterarse de un cambio y siguiera animando sola.
 *
 * ── El alto es mínimo, no fijo ───────────────────────────────────────
 * `min-h-*` y no `h-*`: en inglés el titular ocupa una línea distinta, y
 * un alto fijo lo obligaría a salirse o a encogerse. Y no `100vh`, que en
 * un teléfono cambia de valor al aparecer y desaparecer la barra del
 * navegador — el fondo daría un salto en mitad del desplazamiento.
 *
 * Lo que sí garantiza el alto mínimo es que el vídeo, que está en
 * absoluto, no pueda empujar nada al cargar: la portada ya mide lo que va
 * a medir antes de que llegue el primer byte del MP4.
 */
export function ShopHero() {
  const motionOk = useHeroMotion();

  return (
    /*
     * `isolate` crea el contexto de apilamiento de la portada: los
     * `absolute` de las capas se resuelven aquí dentro y no pueden
     * colarse por encima de la barra superior, que es pegajosa y va en
     * z-50. `overflow-hidden` recorta lo que el parallax saca por arriba
     * y por abajo.
     */
    <section className="relative isolate flex min-h-[560px] flex-col justify-center overflow-hidden bg-ds-ink sm:min-h-[620px] lg:min-h-[680px]">
      <ShopHeroBackdrop motionOk={motionOk} />
      <ShopHeroVeil />

      <div className="relative mx-auto w-full max-w-6xl px-6 py-20 sm:px-8 sm:py-24 lg:py-28">
        <ShopHeroCopy animate={motionOk} />
      </div>

      <ShopHeroScrollCue targetId={SHOP_PRODUCTS_ID} animate={motionOk} />
    </section>
  );
}
